import { useEffect, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, Plus, Heart, MessageSquare, Paperclip, Send, Loader2, ArrowLeft, Lock, Globe,
  FileText, Image as ImageIcon, ListChecks, Check, Hash, Download,
  MoreVertical, Trash2, Ban, Shield, ShieldOff, UserMinus,
} from "lucide-react";
import { toast } from "sonner";

const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/community/files/${id}/download`;

function Attachment({ f }) {
  const token = localStorage.getItem("token");
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    if (!f?.is_image) return;
    let url;
    api.get(`/community/files/${f.id}/download`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setBlob(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [f]);
  if (!f) return null;
  if (f.is_image) {
    return blob ? <img src={blob} alt={f.filename} className="mt-3 rounded-sm max-h-72 border border-border" data-testid="post-image" />
      : <div className="mt-3 h-24 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  return (
    <a href={`${fileUrl(f.id)}?auth=${token}`} target="_blank" rel="noreferrer" download={f.filename}
      className="mt-3 inline-flex items-center gap-2 text-sm border border-border rounded-sm px-3 py-2 hover:border-primary transition-colors" data-testid="post-file">
      <FileText className="w-4 h-4 text-primary" /> {f.filename} <Download className="w-3.5 h-3.5 text-muted-foreground" />
    </a>
  );
}

function Composer({ groupId, onPosted }) {
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!content.trim() && !file) return;
    setBusy(true);
    try {
      let fileId = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/community/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        fileId = up.data.id;
      }
      const r = await api.post("/community/posts", { content: content.trim(), group_id: groupId || null, file_id: fileId });
      setContent(""); setFile(null);
      onPosted(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-4 mb-6" data-testid="composer">
      <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={2} data-testid="composer-input"
        placeholder="Share something with the community…"
        className="w-full bg-muted border border-input px-3 py-2 rounded-sm outline-none focus:border-primary text-sm resize-none" />
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-3">
          <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary" data-testid="composer-attach">
            <Paperclip className="w-4 h-4" /> {file ? file.name.slice(0, 22) : "Attach file"}
          </button>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])}
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.csv,.rtf,.md,.ppt,.pptx,.xls,.xlsx" />
        </div>
        <button onClick={submit} disabled={busy} data-testid="composer-post"
          className="bg-primary text-primary-foreground px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
        </button>
      </div>
    </div>
  );
}

function Post({ p, me, onRemoved }) {
  const [likes, setLikes] = useState(p.likes);
  const [liked, setLiked] = useState(p.liked);
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [ctext, setCtext] = useState("");
  const [menu, setMenu] = useState(false);

  const isMine = me && p.author_id === me.id;
  const toggleLike = async () => {
    setLiked(!liked); setLikes((n) => n + (liked ? -1 : 1));
    try { await api.post(`/community/posts/${p.id}/like`); } catch { setLiked(liked); setLikes(p.likes); }
  };
  const loadComments = async () => {
    setOpen(!open);
    if (comments === null) {
      const r = await api.get(`/community/posts/${p.id}/comments`);
      setComments(r.data);
    }
  };
  const addComment = async () => {
    if (!ctext.trim()) return;
    const r = await api.post(`/community/posts/${p.id}/comments`, { content: ctext.trim() });
    setComments((c) => [...(c || []), r.data]); setCtext("");
  };
  const deletePost = async () => {
    try { await api.delete(`/community/posts/${p.id}`); toast.success("Post removed"); onRemoved?.(p.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const blockAuthor = async () => {
    try {
      await api.post("/community/block", { user_id: p.author_id, user_name: p.author_name });
      toast.success(`Blocked ${p.author_name}`); onRemoved?.(p.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not block"); }
  };
  const deleteComment = async (c) => {
    try { await api.delete(`/community/comments/${c.id}`); setComments((list) => list.filter((x) => x.id !== c.id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="border border-border bg-card p-5 mb-4" data-testid="post">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center font-serif text-primary">{(p.author_name || "?")[0]}</div>
        <div>
          <div className="text-sm font-medium">{p.author_name}</div>
          <div className="text-xs text-muted-foreground font-mono">{new Date(p.created_at).toLocaleString()}</div>
        </div>
        {me && (p.can_delete || !isMine) && (
          <div className="ml-auto relative">
            <button onClick={() => setMenu(!menu)} data-testid={`post-menu-${p.id}`} className="text-muted-foreground hover:text-foreground p-1"><MoreVertical className="w-4 h-4" /></button>
            {menu && (
              <div className="absolute right-0 top-7 z-20 bg-card border border-border rounded-sm shadow-lg min-w-[140px] py-1" onMouseLeave={() => setMenu(false)}>
                {p.can_delete && (
                  <button onClick={deletePost} data-testid={`post-delete-${p.id}`} className="w-full text-left px-3 py-1.5 text-xs text-destructive hover:bg-muted flex items-center gap-2"><Trash2 className="w-3.5 h-3.5" /> Delete post</button>
                )}
                {!isMine && (
                  <button onClick={blockAuthor} data-testid={`post-block-${p.id}`} className="w-full text-left px-3 py-1.5 text-xs text-foreground hover:bg-muted flex items-center gap-2"><Ban className="w-3.5 h-3.5" /> Block user</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {p.content && <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{p.content}</p>}
      {p.file && <Attachment f={p.file} />}
      <div className="flex items-center gap-5 mt-4 text-sm text-muted-foreground">
        <button onClick={toggleLike} data-testid="post-like" className={`flex items-center gap-1.5 hover:text-primary transition-colors ${liked ? "text-primary" : ""}`}>
          <Heart className={`w-4 h-4 ${liked ? "fill-primary" : ""}`} /> {likes}
        </button>
        <button onClick={loadComments} data-testid="post-comment-toggle" className="flex items-center gap-1.5 hover:text-primary transition-colors">
          <MessageSquare className="w-4 h-4" /> {p.comment_count || (comments?.length ?? 0)}
        </button>
      </div>
      {open && (
        <div className="mt-4 border-t border-border pt-3 space-y-3" data-testid="comments">
          {(comments || []).map((c) => (
            <div key={c.id} className="text-sm flex items-start gap-2 group">
              <div className="flex-1"><span className="text-primary font-medium mr-2">{c.author_name}</span><span className="text-foreground/85">{c.content}</span></div>
              {c.can_delete && (
                <button onClick={() => deleteComment(c)} data-testid={`comment-delete-${c.id}`} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <input value={ctext} onChange={(e) => setCtext(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()}
              placeholder="Write a comment…" data-testid="comment-input"
              className="flex-1 bg-muted border border-input px-3 py-1.5 rounded-sm outline-none focus:border-primary text-sm" />
            <button onClick={addComment} data-testid="comment-send" className="text-primary"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function Projects({ gid }) {
  const [projects, setProjects] = useState([]);
  const [title, setTitle] = useState("");
  const [taskInputs, setTaskInputs] = useState({});

  const load = () => api.get(`/community/groups/${gid}/projects`).then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gid]);

  const addProject = async () => {
    if (!title.trim()) return;
    await api.post(`/community/groups/${gid}/projects`, { title: title.trim() }); setTitle(""); load();
  };
  const addTask = async (pid) => {
    const t = (taskInputs[pid] || "").trim(); if (!t) return;
    await api.post(`/community/projects/${pid}/tasks`, { title: t });
    setTaskInputs((s) => ({ ...s, [pid]: "" })); load();
  };
  const toggle = async (task) => { await api.patch(`/community/tasks/${task.id}`, { done: !task.done }); load(); };

  return (
    <div data-testid="projects-tab">
      <div className="flex gap-2 mb-5">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New project name…" data-testid="project-input"
          className="flex-1 bg-muted border border-input px-3 py-2 rounded-sm outline-none focus:border-primary text-sm" />
        <button onClick={addProject} data-testid="project-add" className="bg-primary text-primary-foreground px-4 rounded-sm text-sm flex items-center gap-1.5"><Plus className="w-4 h-4" /> Project</button>
      </div>
      {projects.map((p) => {
        const done = p.tasks.filter((t) => t.done).length;
        return (
          <div key={p.id} className="border border-border bg-card p-4 mb-4" data-testid="project">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-serif text-xl">{p.title}</h4>
              <span className="text-xs font-mono text-muted-foreground">{done}/{p.tasks.length} done</span>
            </div>
            <div className="space-y-1.5 mb-3">
              {p.tasks.map((t) => (
                <button key={t.id} onClick={() => toggle(t)} data-testid="task" className="flex items-center gap-2 text-sm w-full text-left hover:text-primary">
                  <span className={`w-4 h-4 border rounded-sm flex items-center justify-center ${t.done ? "bg-primary border-primary" : "border-input"}`}>{t.done && <Check className="w-3 h-3 text-primary-foreground" />}</span>
                  <span className={t.done ? "line-through text-muted-foreground" : ""}>{t.title}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={taskInputs[p.id] || ""} onChange={(e) => setTaskInputs((s) => ({ ...s, [p.id]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addTask(p.id)} placeholder="Add a task…" data-testid="task-input"
                className="flex-1 bg-muted border border-input px-3 py-1.5 rounded-sm outline-none focus:border-primary text-sm" />
              <button onClick={() => addTask(p.id)} data-testid="task-add" className="text-primary"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        );
      })}
      {projects.length === 0 && <p className="text-muted-foreground text-sm">No projects yet — start one above.</p>}
    </div>
  );
}

function Members({ group, me, onChanged }) {
  const setRole = async (uid, role) => {
    try { await api.patch(`/community/groups/${group.id}/members/${uid}/role`, { role }); toast.success("Role updated"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const remove = async (uid, name) => {
    try { await api.delete(`/community/groups/${group.id}/members/${uid}`); toast.success(`Removed ${name}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div className="space-y-2" data-testid="members-tab">
      {(group.members || []).map((m) => (
        <div key={m.user_id} className="flex items-center gap-3 border border-border bg-card px-4 py-3" data-testid={`member-${m.user_id}`}>
          <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center font-serif text-primary text-sm">{(m.user_name || "?")[0]}</div>
          <span className="text-sm">{m.user_name}{m.user_id === me?.id && " (you)"}</span>
          <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 border ${m.role === "owner" ? "border-primary text-primary" : m.role === "moderator" ? "border-amber-400/50 text-amber-300" : "border-border text-muted-foreground"}`}>{m.role}</span>
          <div className="ml-auto flex items-center gap-2">
            {group.is_owner && m.role !== "owner" && (
              m.role === "moderator" ? (
                <button onClick={() => setRole(m.user_id, "member")} data-testid={`demote-${m.user_id}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ShieldOff className="w-3.5 h-3.5" /> Demote</button>
              ) : (
                <button onClick={() => setRole(m.user_id, "moderator")} data-testid={`promote-${m.user_id}`} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Make mod</button>
              )
            )}
            {group.can_moderate && m.role !== "owner" && m.user_id !== me?.id && (
              <button onClick={() => remove(m.user_id, m.user_name)} data-testid={`remove-member-${m.user_id}`} className="text-xs text-destructive hover:opacity-80 flex items-center gap-1"><UserMinus className="w-3.5 h-3.5" /> Remove</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function BlockedBar({ onChange }) {
  const [blocks, setBlocks] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/community/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const unblock = async (uid) => {
    await api.post("/community/unblock", { user_id: uid });
    toast.success("Unblocked"); load(); onChange?.();
  };
  if (blocks.length === 0) return null;
  return (
    <div className="mb-5 border border-border bg-card rounded-sm" data-testid="blocked-bar">
      <button onClick={() => setOpen(!open)} data-testid="blocked-toggle" className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground">
        <Ban className="w-3.5 h-3.5" /> Blocked users ({blocks.length})
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2 border-t border-border pt-3">
          {blocks.map((b) => (
            <div key={b.user_id} className="flex items-center justify-between text-sm" data-testid={`blocked-${b.user_id}`}>
              <span>{b.user_name || b.user_id}</span>
              <button onClick={() => unblock(b.user_id)} data-testid={`unblock-${b.user_id}`} className="text-xs text-primary hover:underline">Unblock</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const [tab, setTab] = useState("feed");
  const [feed, setFeed] = useState([]);
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null); // group detail
  const [gtab, setGtab] = useState("posts");
  const [gposts, setGposts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", privacy: "public" });
  const [joinCode, setJoinCode] = useState("");

  const loadFeed = () => api.get("/community/feed").then((r) => setFeed(r.data)).catch(() => {});
  const loadGroups = () => api.get("/community/groups").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { loadFeed(); loadGroups(); }, []);

  const openGroup = async (g) => {
    try {
      const r = await api.get(`/community/groups/${g.id}`);
      setActive(r.data); setGtab("posts");
      const pr = await api.get(`/community/groups/${g.id}/posts`);
      setGposts(pr.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot open group"); }
  };
  const createGroup = async () => {
    if (form.name.trim().length < 2) return toast.error("Name too short");
    const r = await api.post("/community/groups", form);
    setShowCreate(false); setForm({ name: "", description: "", privacy: "public" });
    loadGroups(); openGroup(r.data);
  };
  const join = async (g) => {
    try {
      await api.post(`/community/groups/${g.id}/join`, g.privacy === "private" ? { invite_code: joinCode } : {});
      toast.success("Joined"); loadGroups(); openGroup(g);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not join"); }
  };
  const deleteFile = async (fid) => {
    try { await api.delete(`/community/groups/${active.id}/files/${fid}`); toast.success("File removed"); openGroup(active); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Users className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">Community</h1>
        </div>
        <p className="text-muted-foreground mb-8">Connect with fellow members, share files, and build together in your own groups.</p>

        {!active ? (
          <>
            <div className="flex gap-2 mb-6">
              <button onClick={() => setTab("feed")} data-testid="tab-feed" className={`px-4 py-2 text-xs uppercase tracking-widest font-mono border ${tab === "feed" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>Feed</button>
              <button onClick={() => setTab("groups")} data-testid="tab-groups" className={`px-4 py-2 text-xs uppercase tracking-widest font-mono border ${tab === "groups" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>Groups</button>
            </div>

            {tab === "feed" ? (
              <>
                <BlockedBar onChange={loadFeed} />
                <Composer onPosted={(p) => setFeed((f) => [p, ...f])} />
                {feed.map((p) => <Post key={p.id} p={p} me={user} onRemoved={(id) => setFeed((f) => f.filter((x) => x.id !== id))} />)}
                {feed.length === 0 && <p className="text-muted-foreground text-sm">No posts yet — be the first.</p>}
              </>
            ) : (
              <>
                <button onClick={() => setShowCreate(!showCreate)} data-testid="create-group-toggle"
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-sm text-sm mb-5"><Plus className="w-4 h-4" /> Create a group</button>
                {showCreate && (
                  <div className="border border-border bg-card p-4 mb-6 space-y-3" data-testid="create-group-form">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Group name" data-testid="group-name"
                      className="w-full bg-muted border border-input px-3 py-2 rounded-sm outline-none focus:border-primary text-sm" />
                    <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's it about?" data-testid="group-desc"
                      className="w-full bg-muted border border-input px-3 py-2 rounded-sm outline-none focus:border-primary text-sm" />
                    <div className="flex items-center gap-4 text-sm">
                      <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={form.privacy === "public"} onChange={() => setForm({ ...form, privacy: "public" })} /> <Globe className="w-4 h-4" /> Public</label>
                      <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={form.privacy === "private"} onChange={() => setForm({ ...form, privacy: "private" })} data-testid="group-private" /> <Lock className="w-4 h-4" /> Private (invite-only)</label>
                    </div>
                    <button onClick={createGroup} data-testid="group-create-submit" className="bg-primary text-primary-foreground px-4 py-2 rounded-sm text-sm">Create</button>
                  </div>
                )}
                <div className="space-y-3">
                  {groups.map((g) => (
                    <div key={g.id} className="border border-border bg-card p-4 flex items-center justify-between gap-3" data-testid="group-row">
                      <div className="min-w-0 cursor-pointer" onClick={() => (g.member || g.privacy === "public") && openGroup(g)}>
                        <div className="flex items-center gap-2">
                          {g.privacy === "private" ? <Lock className="w-4 h-4 text-primary" /> : <Hash className="w-4 h-4 text-primary" />}
                          <h3 className="font-serif text-xl truncate">{g.name}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{g.description || "—"} · {g.member_count} member{g.member_count !== 1 ? "s" : ""}</p>
                      </div>
                      {g.member ? (
                        <button onClick={() => openGroup(g)} data-testid={`open-group-${g.id}`} className="text-sm text-primary shrink-0">Open →</button>
                      ) : g.privacy === "private" ? (
                        <div className="flex gap-1 shrink-0">
                          <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="code" className="w-20 bg-muted border border-input px-2 py-1 rounded-sm text-xs" data-testid="join-code" />
                          <button onClick={() => join(g)} className="text-sm text-primary" data-testid={`join-group-${g.id}`}>Join</button>
                        </div>
                      ) : (
                        <button onClick={() => join(g)} data-testid={`join-group-${g.id}`} className="text-sm text-primary shrink-0">Join</button>
                      )}
                    </div>
                  ))}
                  {groups.length === 0 && <p className="text-muted-foreground text-sm">No groups yet — create the first.</p>}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <button onClick={() => { setActive(null); loadGroups(); }} data-testid="back-to-community" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4"><ArrowLeft className="w-4 h-4" /> All groups</button>
            <div className="flex items-center gap-2 mb-1">
              {active.privacy === "private" ? <Lock className="w-5 h-5 text-primary" /> : <Hash className="w-5 h-5 text-primary" />}
              <h2 className="font-serif text-3xl">{active.name}</h2>
            </div>
            <p className="text-muted-foreground text-sm mb-4">{active.description} · {active.member_count} members</p>
            {active.is_owner && active.privacy === "private" && active.invite_code && (
              <div className="text-xs font-mono text-muted-foreground mb-4 border border-border rounded-sm inline-block px-3 py-1.5" data-testid="invite-code">Invite code: <span className="text-primary">{active.invite_code}</span></div>
            )}
            <div className="flex gap-2 mb-6">
              {["posts", "files", "projects", "members"].map((t) => (
                <button key={t} onClick={() => setGtab(t)} data-testid={`gtab-${t}`} className={`px-4 py-2 text-xs uppercase tracking-widest font-mono border capitalize ${gtab === t ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>{t}</button>
              ))}
            </div>

            {gtab === "posts" && (active.member ? (
              <>
                <Composer groupId={active.id} onPosted={(p) => setGposts((g) => [p, ...g])} />
                {gposts.map((p) => <Post key={p.id} p={p} me={user} onRemoved={(id) => setGposts((g) => g.filter((x) => x.id !== id))} />)}
                {gposts.length === 0 && <p className="text-muted-foreground text-sm">No posts in this group yet.</p>}
              </>
            ) : <p className="text-muted-foreground text-sm">Join this group to see and add posts.</p>)}

            {gtab === "files" && (
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))" }} data-testid="files-tab">
                {(active.files || []).map((f) => (
                  <div key={f.id} className="relative border border-border bg-card p-4 flex items-center gap-2 hover:border-primary text-sm group">
                    <a href={`${fileUrl(f.id)}?auth=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" download={f.filename} className="flex items-center gap-2 min-w-0 flex-1">
                      {f.is_image ? <ImageIcon className="w-4 h-4 text-primary shrink-0" /> : <FileText className="w-4 h-4 text-primary shrink-0" />}
                      <span className="truncate">{f.filename}</span>
                    </a>
                    {active.can_moderate && (
                      <button onClick={() => deleteFile(f.id)} data-testid={`file-delete-${f.id}`} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                {(active.files || []).length === 0 && <p className="text-muted-foreground text-sm">No files yet — attach files to posts to build the group library.</p>}
              </div>
            )}

            {gtab === "projects" && (active.member ? <Projects gid={active.id} /> : <p className="text-muted-foreground text-sm">Join this group to collaborate on projects.</p>)}

            {gtab === "members" && (active.member || active.is_owner ? (
              <Members group={active} me={user} onChanged={() => openGroup(active)} />
            ) : <p className="text-muted-foreground text-sm">Join this group to see members.</p>)}
          </>
        )}
      </div>
    </PageShell>
  );
}
